import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiEdit, FiTrash2, FiArrowLeft, FiMapPin, FiPhone, FiMail,
  FiClock, FiUser, FiUsers, FiCalendar
} from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { confirmDelete } from '../utils/swal';

const BranchDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranchDetails();
  }, [id]);

  const fetchBranchDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/branches/${id}`);
      setBranch(response.data.data);
    } catch (error) {
      console.error('Error fetching branch details:', error);
      toast.error('Failed to load branch details');
      navigate('/branches');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!branch) return;

    const confirmed = await confirmDelete(branch.name, 'branch');
    if (!confirmed) return;

    try {
      await api.delete(`/branches/${id}`);
      toast.success('Branch deleted successfully');
      navigate('/branches');
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error(error.response?.data?.message || 'Failed to delete branch');
    }
  };

  const toggleStatus = async () => {
    try {
      await api.patch(`/branches/${id}/status`, { is_active: !branch.is_active });
      toast.success(`Branch ${!branch.is_active ? 'activated' : 'deactivated'} successfully`);
      fetchBranchDetails();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update branch status');
    }
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!branch) {
    return null;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/branches')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <FiArrowLeft /> Back to Branches
        </button>

        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-800">{branch.name}</h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  branch.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {branch.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-gray-600">Branch Code: {branch.code}</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/branches/edit/${id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit /> Edit
            </button>
            <button
              onClick={toggleStatus}
              className={`px-4 py-2 rounded-lg transition-colors ${
                branch.is_active
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {branch.is_active ? 'Deactivate' : 'Activate'}
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiTrash2 /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Contact Information
            </h2>
            <div className="space-y-4">
              {branch.address && (
                <div className="flex items-start gap-3">
                  <FiMapPin className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="text-gray-800">
                      {branch.address}
                      {branch.city && <>, {branch.city}</>}
                      {branch.state && <>, {branch.state}</>}
                      {branch.pincode && <> - {branch.pincode}</>}
                    </p>
                  </div>
                </div>
              )}

              {branch.phone && (
                <div className="flex items-start gap-3">
                  <FiPhone className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-gray-800">{branch.phone}</p>
                  </div>
                </div>
              )}

              {branch.email && (
                <div className="flex items-start gap-3">
                  <FiMail className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-800">{branch.email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Manager Information */}
          {(branch.manager_name || branch.manager_phone) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Branch Manager
              </h2>
              <div className="space-y-4">
                {branch.manager_name && (
                  <div className="flex items-start gap-3">
                    <FiUser className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="text-gray-800 font-semibold">{branch.manager_name}</p>
                    </div>
                  </div>
                )}

                {branch.manager_phone && (
                  <div className="flex items-start gap-3">
                    <FiPhone className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="text-gray-800">{branch.manager_phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Operating Hours */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Operating Hours
            </h2>
            <div className="flex items-center gap-3">
              <FiClock className="text-gray-400" size={20} />
              <div>
                <p className="text-sm text-gray-500">Daily Timing</p>
                <p className="text-gray-800 font-semibold">
                  {formatTime(branch.opening_time)} - {formatTime(branch.closing_time)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Statistics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Statistics
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FiUsers className="text-blue-600" size={24} />
                  <div>
                    <p className="text-sm text-gray-600">Total Members</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {branch.total_members || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FiUsers className="text-green-600" size={24} />
                  <div>
                    <p className="text-sm text-gray-600">Total Trainers</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {branch.total_trainers || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Quick Actions
            </h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate(`/members?branch_id=${id}`)}
                className="w-full text-left px-4 py-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              >
                View Members
              </button>
              <button
                onClick={() => navigate(`/trainers?branch_id=${id}`)}
                className="w-full text-left px-4 py-3 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
              >
                View Trainers
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Metadata
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <FiCalendar className="flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p>{formatDate(branch.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <FiCalendar className="flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Last Updated</p>
                  <p>{formatDate(branch.updated_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchDetail;
